import React, { useEffect, useRef, useState } from "react";
import "./NumberInput.css";
import debounce from "lodash.debounce";

const round1 = (n) => Math.round(n * 10) / 10;

const Number_point_Input = ({
  label,
  postLabel,
  onChange,
  value,
  min,
  max,
  onFocusOut,
  disabled,
  step,
  key,
  comp,
}) => {
  // Same working pattern as the main NumberInput: localValue = instant display,
  // valueRef = synchronous math base (rapid clicks never skip / revert),
  // interactingRef ignores the parent's laggy echo briefly. Button steps are
  // rounded to 1 decimal so repeated 0.1 clicks stay clean.
  const [localValue, setLocalValue] = useState(value);
  const valueRef = useRef(value);
  const interactingRef = useRef(false);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    if (!interactingRef.current) {
      valueRef.current = value;
      setLocalValue(value);
    }
  }, [value]);

  const debouncedFocusOutRef = useRef(
    debounce(() => {
      if (onFocusOut) onFocusOut();
    }, 1000)
  );

  useEffect(() => {
    return () => {
      debouncedFocusOutRef.current.cancel();
      clearTimeout(idleTimerRef.current);
    };
  }, []);

  const markInteracting = () => {
    interactingRef.current = true;
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      interactingRef.current = false;
    }, 400);
  };

  const updateValue = (newValue) => {
    if (disabled || !onChange) return;
    let clampedValue = Math.max(
      isNaN(min) ? -Infinity : Number(min),
      Math.min(isNaN(max) ? Infinity : Number(max), newValue)
    );
    clampedValue = round1(clampedValue);
    if (clampedValue === Number(valueRef.current)) return;
    valueRef.current = clampedValue;
    setLocalValue(clampedValue);
    markInteracting();
    onChange(clampedValue);
    debouncedFocusOutRef.current();
  };

  const handleInc = () => {
    if (disabled || !onChange) return;
    updateValue(Number(valueRef.current) + 0.1);
  };

  const handleDec = () => {
    if (disabled || !onChange) return;
    updateValue(Number(valueRef.current) - 0.1);
  };

  const handleChange = (evt) => {
    if (disabled || !onChange) return;
    const inputVal = evt.target.value;
    setLocalValue(inputVal);
    markInteracting();
    if (inputVal !== "" && !isNaN(inputVal)) {
      valueRef.current = Number(inputVal);
      onChange(Number(inputVal));
    }
  };

  return (
    <div className="main-div">
      {label && <span className="number-label">{label}</span>}
      <div className={"btn-div-font " + (disabled ? "disabled" : "")}>
        <div className="main-btn">
          <div onClick={handleInc} className="top-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon icon-tabler icon-tabler-chevron-up"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <polyline points="6 15 12 9 18 15"></polyline>
            </svg>
          </div>
          <div onClick={handleDec} className="down-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon icon-tabler icon-tabler-chevron-down"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
        <span>
          <input
            type="number"
            className="form-control"
            value={localValue}
            step={step}
            min={min}
            max={max}
            key={key}
            onChange={handleChange}
            onBlur={onFocusOut}
          />
          {postLabel && <span className="value">{postLabel}</span>}
        </span>
      </div>
    </div>
  );
};

export default Number_point_Input;